import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Building2, Globe, Shield, Zap } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const About = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const aboutRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (aboutRef.current) {
      observer.observe(aboutRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: Shield,
      title: t('about.values.transparency.title'),
      description: t('about.values.transparency.description'),
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?q=80&w=2340',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Zap,
      title: t('about.values.technology.title'),
      description: t('about.values.technology.description'),
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?q=80&w=2125',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Building2,
      title: t('about.values.minimalism.title'),
      description: t('about.values.minimalism.description'),
      image: 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?q=80&w=2340',
      color: 'from-gray-600 to-gray-700'
    },
    {
      icon: Globe,
      title: t('about.values.quality.title'),
      description: t('about.values.quality.description'),
      image: 'https://images.unsplash.com/photo-1464207687429-7505649dae38?q=80&w=2346',
      color: 'from-green-500 to-green-600'
    }
  ];

  return (
    <section id="about" className="py-24 bg-white overflow-hidden" ref={aboutRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
            {t('about.title')}
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            {t('about.description')}
          </p>
        </div>

        {/* Mission Statement with Background */}
        <div className={`relative bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl p-12 mb-16 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px'
            }}></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto text-center">
            <h3 className="text-2xl md:text-3xl font-light text-gray-900 mb-6">
              {t('about.missionTitle')}
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed">
              {t('about.missionText')}
            </p>
          </div>
        </div>

        {/* Enhanced Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => {
            const IconComponent = value.icon;
            return (
              <div
                key={index}
                className={`group transition-all duration-700 delay-${index * 100} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
              >
                <Card className="relative border-0 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 overflow-hidden h-full">
                  {/* Background Image */}
                  <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity duration-500">
                    <img 
                      src={value.image} 
                      alt={value.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${value.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                  
                  <CardContent className="relative p-8 text-center h-full flex flex-col justify-center">
                    {/* Animated Icon */}
                    <div className="relative mb-6">
                      <div className={`w-16 h-16 mx-auto bg-gradient-to-br ${value.color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent size={28} className="text-white" />
                      </div>
                      {/* Pulse effect */}
                      <div className={`absolute inset-0 w-16 h-16 mx-auto bg-gradient-to-br ${value.color} rounded-xl opacity-0 group-hover:opacity-30 group-hover:scale-125 transition-all duration-500`}></div>
                    </div>
                    
                    <h4 className="text-xl font-medium text-gray-900 mb-4 group-hover:text-gray-800 transition-colors">
                      {value.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors">
                      {value.description}
                    </p>
                    
                    {/* Bottom accent line */}
                    <div className={`w-0 group-hover:w-12 h-0.5 bg-gradient-to-r ${value.color} mx-auto mt-4 transition-all duration-500`}></div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default About;